import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';

/**
 * "Add a video or post" in the composer, as in the app (voucha-app src/components/EmbedPicker.tsx): paste a YouTube,
 * X or Facebook link, see what it is, then add it. The link goes in on a line of its own, which is how FoF
 * Formatting turns it into a player or a post on the website and in the app. Any other link goes in as a plain link.
 */

// Same patterns as the app (voucha-app src/lib/embeds.ts), so both treat a link the same way
const YT = /^(?:https?:\/\/)?(?:www\.|m\.|music\.)?(?:youtube\.com\/(?:watch\?(?:[^#]*&)?v=|shorts\/|live\/|embed\/)|youtu\.be\/)([\w-]{11})/i;
const X = /^(?:https?:\/\/)?(?:www\.|mobile\.)?(?:twitter|x)\.com\/(?:[\w]+|i\/web|i)\/status(?:es)?\/(\d+)/i;
const FB = /^(?:https?:\/\/)?(?:www\.|m\.|web\.)?(?:facebook\.com|fb\.watch)\/\S+/i;
const LINK = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i;

/** What a pasted link is: { kind, id, url } for a YouTube video, an X post or a Facebook post or video; null otherwise */
export function parseEmbed(raw) {
  const url = String(raw || '').trim();
  let m1 = YT.exec(url);
  if (m1) return {
    kind: 'youtube',
    id: m1[1],
    url: 'https://www.youtube.com/watch?v=' + m1[1]
  };
  m1 = X.exec(url);
  if (m1) return {
    kind: 'x',
    id: m1[1],
    url: 'https://x.com/i/status/' + m1[1]
  };
  if (FB.test(url)) return {
    kind: 'facebook',
    id: url,
    url: /^https?:/i.test(url) ? url : 'https://' + url
  };
  return null;
}

/** Put a link at the cursor on a line of its own (a blank line before and after, unless there already is one) */
export function insertLink(url) {
  const editor = app.composer && app.composer.editor;
  if (!editor) return;
  const text = String((editor.el && editor.el.value) ?? (app.composer.fields && app.composer.fields.content()) ?? '');
  const range = editor.getSelectionRange ? editor.getSelectionRange() : [text.length, text.length];
  const start = range[0];
  const end = range[1];
  const before = text.slice(0, start);
  const after = text.slice(end);
  const lead = before.trim() ? /\n\n\s*$/.test(before) ? '' : /\n\s*$/.test(before) ? '\n' : '\n\n' : '';
  const trail = /^\s*\n\n/.test(after) ? '' : /^\s*\n/.test(after) ? '\n' : '\n\n';
  const insert = lead + url + trail;
  if (editor.insertBetween) editor.insertBetween(start, end, insert, false);else editor.insertAtCursor(insert, false);
  if (editor.focus) editor.focus();
}
const SITES = [['youtube', 'fab fa-youtube', 'YouTube'], ['x', 'fab fa-x-twitter', 'X'], ['facebook', 'fab fa-facebook', 'Facebook']];
export class EmbedLinkModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.url = '';
  }
  className() {
    return 'VouchaEmbedModal Modal--small';
  }
  title() {
    return 'Add a video or post';
  }
  content() {
    const embed = parseEmbed(this.url);
    const plain = !embed && LINK.test(this.url.trim());
    return m('.Modal-body', [m('p.VouchaEmbedModal-intro', 'Paste a link from YouTube, X or Facebook. It shows in your post as a video or post people can see without leaving Voucha.'), m('.Form-group', [m('input.FormControl', {
      type: 'url',
      inputmode: 'url',
      autocomplete: 'off',
      placeholder: 'https://youtu.be/…',
      value: this.url,
      oninput: e => this.url = e.target.value,
      onkeydown: e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.add();
        }
      },
      oncreate: vnode => setTimeout(() => vnode.dom.focus(), 50),
      'aria-label': 'Link to a video or post'
    })]), m('.VouchaEmbedModal-sites', SITES.map(_ref => {
      let [kind, icon, name] = _ref;
      return m('span.VouchaEmbedModal-site' + (embed && embed.kind === kind ? '.is-on' : ''), [m('i', {
        className: icon,
        'aria-hidden': 'true'
      }), ' ', name]);
    })), embed ? this.preview(embed) : plain ? m('p.VouchaEmbedModal-note', 'Not a YouTube, X or Facebook link: it will be added as an ordinary link.') : null, m('.Form-group.VouchaEmbedModal-actions', [Button.component({
      className: 'Button Button--primary',
      disabled: !embed && !plain,
      onclick: () => this.add()
    }, 'Add to post')])]);
  }

  /** What the link will show as: YouTube's own thumbnail, or a card for an X or Facebook post */
  preview(embed) {
    if (embed.kind === 'youtube') {
      return m('.VouchaEmbedModal-preview.is-video', [m('img', {
        src: 'https://i.ytimg.com/vi/' + embed.id + '/hqdefault.jpg',
        alt: '',
        loading: 'lazy'
      }), m('span.VouchaEmbedModal-play', m('i.fas.fa-play', {
        'aria-hidden': 'true'
      }))]);
    }
    const site = SITES.find(s => s[0] === embed.kind);
    return m('.VouchaEmbedModal-preview.is-card', [m('i', {
      className: site[1],
      'aria-hidden': 'true'
    }), m('span', [m('strong', embed.kind === 'x' ? 'Post on X' : 'Facebook post or video'), m('small', embed.url)])]);
  }
  add() {
    const embed = parseEmbed(this.url);
    const raw = this.url.trim();
    if (!embed && !LINK.test(raw)) return;
    const url = embed ? embed.url : /^https?:\/\//i.test(raw) ? raw : 'https://' + raw;
    this.hide();
    // after the window has closed, so the cursor is back in the composer
    setTimeout(() => insertLink(url), 0);
  }
}

/** Open the window from the composer's toolbar */
export function openEmbedLink() {
  app.modal.show(EmbedLinkModal);
}